import { useAuth } from '#core/Auth';
import { useInstitution } from '#core/Institution';
import { usePageQuery } from '#core/PageQuery';
import { ImpactProduct } from '#core/PageQuery/contexts/PageQueryContext';
import { usePermissions } from '#core/Permissions';
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { SDG_MAX_INDEX } from '../utils/constants';

export const sdgImpactRoleNames = [
  'SDG_IMPACT_SUBSCRIPTION_PREMIUM',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED',
  'SDG_IMPACT_SUBSCRIPTION_BASIC',
] as const;

export const sdgImpactLegacyRoleNames = [
  'SDG_IMPACT_SUBSCRIPTION',
  'SDG_IMPACT_RELEASE',
] as const;

export type SdgImpactRoleNames = typeof sdgImpactRoleNames[number];
export type SdgImpactLegacyRoleNames = typeof sdgImpactLegacyRoleNames[number];

const impactRolesToProductsMap = new Map<
  SdgImpactRoleNames | SdgImpactLegacyRoleNames,
  ImpactProduct
>([
  ['SDG_IMPACT_SUBSCRIPTION', 'impact'],
  ['SDG_IMPACT_RELEASE', 'impact_unpaid'],
  ['SDG_IMPACT_SUBSCRIPTION_BASIC', 'impact_basic'],
  ['SDG_IMPACT_SUBSCRIPTION_ADVANCED', 'impact_advanced'],
  ['SDG_IMPACT_SUBSCRIPTION_PREMIUM', 'impact_premium'],
]);

export type ImpactIframeContract = {
  selectedYear: number;
  rawYears: number[];
  releasedYears: number[];
  setReleasedYears: (values: number[]) => void;
  setSelectedYear: (value: number) => void;
  setRawYears: (values: number[]) => void;
  impactReleaseDates: Record<string, boolean | Date>;
  impactEmbargoEndDates: Record<string, boolean | Date>;
  availableImpactYears: number[];
  availableDetailsPageYears: number[];
  isAfterEmbargoDate: Record<string, boolean | Date>;
  hasSdgAccess: (
    sdgId: number | string,
    sdgYear: number | string,
    sdgYears: number[],
  ) => boolean;
  getFirstAccessibleSdg: (
    years: number[],
  ) => Record<string, number> | undefined;
};

export const defaultImpactIframeContext = Object.freeze({
  selectedYear: new Date().getFullYear(),
  rawYears: [],
  releasedYears: [],
  setReleasedYears: () => {},
  setSelectedYear: () => {},
  setRawYears: () => {},
  availableImpactYears: [],
  availableDetailsPageYears: [],
  isAfterEmbargoDate: {},
  impactReleaseDates: {},
  impactEmbargoEndDates: {},
  hasSdgAccess: () => false,
  getFirstAccessibleSdg: () => undefined,
});

type GetAvailableImpactYears = {
  isAfterEmbargoDate: Record<string, boolean | Date>;
  isAfterReleaseDate: Record<string, boolean | Date>;
  canViewImpactEmbargo: boolean;
  canUseElevatedPermissions: boolean;
  useElevatedPermissions: boolean;
};

const ImpactIframeContext = createContext<ImpactIframeContract>(
  defaultImpactIframeContext,
);

const getUserProduct = (
  userRole: SdgImpactRoleNames | SdgImpactLegacyRoleNames | null,
  rolesToProductsMap: Map<
    SdgImpactRoleNames | SdgImpactLegacyRoleNames,
    ImpactProduct
  >,
  canUseElevatedPermissions: boolean,
): ImpactProduct => {
  if (canUseElevatedPermissions) return 'impact_premium';

  return (
    (userRole ? rolesToProductsMap.get(userRole) : null) ?? 'impact_unpaid'
  );
};

const getAvailableImpactYears = ({
  isAfterEmbargoDate,
  isAfterReleaseDate,
  canViewImpactEmbargo,
  canUseElevatedPermissions,
  useElevatedPermissions,
}: GetAvailableImpactYears) => {
  const allYears = Object.keys(isAfterReleaseDate).map((year) => Number(year));

  const releasedYears = Object.entries(isAfterReleaseDate)
    .map((entry) => (entry.includes(true) ? Number(entry[0]) : null))
    .filter(Boolean) as number[];

  const allNonEmargoedYears = Object.entries(isAfterEmbargoDate)
    .map((entry) => (entry.includes(true) ? Number(entry[0]) : null))
    .filter(Boolean) as number[];

  /* 
 If the user is using elevated permissions
 return the latest impact year even if the release date hasn't passed
 */
  if (canUseElevatedPermissions && useElevatedPermissions)
    return allYears.reverse();

  /* 
 If the user can view impact during embargo
 return the latest impact year even if the release date hasn't passed
 */
  if (canViewImpactEmbargo) return releasedYears.reverse();

  /* 
  return the years the embargo has passed for
 */
  return allNonEmargoedYears.reverse();
};

const getAvailableDetailsPageYears = ({
  isAfterEmbargoDate,
  isAfterReleaseDate,
  canUseElevatedPermissions,
  useElevatedPermissions,
}: Omit<GetAvailableImpactYears, 'canViewImpactEmbargo'>) => {
  const allYears = Object.keys(isAfterReleaseDate).map((year) => Number(year));

  const allNonEmargoedYears = Object.entries(isAfterEmbargoDate)
    .map(([year, allowed]) => (allowed ? Number(year) : null))
    .filter(Boolean) as number[];

  /* 
 If the user is using elevated permissions
 return the latest impact year even if the release date hasn't passed
 */
  if (canUseElevatedPermissions && useElevatedPermissions)
    return allYears.reverse();

  /* 
  return the years the embargo has passed for
 */
  return allNonEmargoedYears.reverse();
};

export const useImpact = () =>
  useContext<ImpactIframeContract>(ImpactIframeContext);

const ImpactProvider: React.FC<{}> = ({ children }) => {
  const {
    query: { selectedYear: year, impactReleaseDates },
  } = usePageQuery();

  const { institution } = useInstitution();

  const { hasResourcePermissionNew, getFirstRoleOfUser } = usePermissions();

  const userRole = getFirstRoleOfUser<
    SdgImpactRoleNames | SdgImpactLegacyRoleNames
  >(
    [...sdgImpactRoleNames, ...sdgImpactLegacyRoleNames],
    institution.institutionId,
  );

  const { canUseElevatedPermissions, useElevatedPermissions } = useAuth();

  const userImpactProduct = getUserProduct(
    userRole,
    impactRolesToProductsMap,
    canUseElevatedPermissions,
  );

  const impactProduct = impactReleaseDates[userImpactProduct];

  const {
    isAfterEmbargoDate,
    isAfterReleaseDate,
    releaseDates,
    embargoEndDates,
  } = impactProduct;

  const canViewImpactEmbargo = useMemo(
    () =>
      hasResourcePermissionNew({
        action: 'sdgImpact:viewPage:overview',
        institutionId: institution.institutionId,
        year,
      }),
    [year, institution, hasResourcePermissionNew],
  );

  const availableImpactYears = getAvailableImpactYears({
    isAfterEmbargoDate,
    isAfterReleaseDate,
    canViewImpactEmbargo,
    canUseElevatedPermissions,
    useElevatedPermissions,
  });

  const availableDetailsPageYears = getAvailableDetailsPageYears({
    isAfterEmbargoDate,
    isAfterReleaseDate,
    canUseElevatedPermissions,
    useElevatedPermissions,
  });

  const hasSdgAccess = useCallback(
    (sdgId: number | string, sdgYear: number | string, sdgYears: number[]) =>
      hasResourcePermissionNew({
        action: 'sdgImpact:viewSdg',
        year: Number(sdgYear),
        institutionId: institution?.institutionId,
        sdgId: Number(sdgId),
        availableImpactYears: sdgYears || [],
      }),
    [institution?.institutionId, hasResourcePermissionNew],
  );

  const getFirstAccessibleSdg = useCallback(
    (yearsToSearch: number[]) => {
      for (const yearToSearch of yearsToSearch) {
        for (let id = 1; id <= SDG_MAX_INDEX; id++) {
          if (hasSdgAccess(id, yearToSearch, yearsToSearch)) {
            return { id, year: yearToSearch };
          }
        }
      }
      return undefined;
    },
    [hasSdgAccess],
  );

  return (
    <ImpactIframeContext.Provider
      value={{
        ...defaultImpactIframeContext,
        impactReleaseDates: releaseDates,
        impactEmbargoEndDates: embargoEndDates,
        availableImpactYears,
        isAfterEmbargoDate,
        availableDetailsPageYears,
        hasSdgAccess,
        getFirstAccessibleSdg,
      }}
    >
      {children}
    </ImpactIframeContext.Provider>
  );
};

export default ImpactProvider;
